// src/components/reports/CategoryFilter.tsx
import React, { useState, useEffect } from 'react';

interface CategoryOption {
  categoryId: string;
  name: string;
  parentCategoryId?: string;
}

interface CategoryFilterProps {
  categories: CategoryOption[];
  selectedCategoryId?: string;
  selectedSubcategoryId?: string;
  onCategoryChange: (categoryId: string) => void;
  onSubcategoryChange: (subcategoryId: string) => void;
  allowAll?: boolean;
  className?: string;
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  selectedCategoryId,
  selectedSubcategoryId,
  onCategoryChange,
  onSubcategoryChange,
  allowAll = true,
  className = "",
}) => {
  const [availableSubcategories, setAvailableSubcategories] = useState<CategoryOption[]>([]);

  // Get main categories (no parent)
  const mainCategories = categories.filter(cat => !cat.parentCategoryId);

  // Update subcategories when main category changes
useEffect(() => {
  if (selectedCategoryId) {
    const subcategories = categories.filter(cat => cat.parentCategoryId === selectedCategoryId);
    setAvailableSubcategories(subcategories);
    
    // Clear subcategory selection if it's not valid for the new category
    if (selectedSubcategoryId && !subcategories.find(sub => sub.categoryId === selectedSubcategoryId)) {
      onSubcategoryChange('');
    }
  } else {
    setAvailableSubcategories([]);
    onSubcategoryChange('');
  }
}, [selectedCategoryId, categories, selectedSubcategoryId]);

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        Filter by Category
      </label>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Main Category */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Category
          </label>
          <select
            value={selectedCategoryId || ''}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            {allowAll && <option key="all-categories" value="">All Categories</option>}
            {mainCategories.map((category) => (
              <option key={category.categoryId} value={category.categoryId}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Subcategory */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Subcategory
          </label>
          <select
            value={selectedSubcategoryId || ''}
            onChange={(e) => onSubcategoryChange(e.target.value)}
            disabled={!selectedCategoryId || availableSubcategories.length === 0}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option key="all-subcategories" value="">All Subcategories</option>
            {availableSubcategories.map((subcategory) => (
              <option key={subcategory.categoryId} value={subcategory.categoryId}>
                {subcategory.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {selectedCategoryId && availableSubcategories.length === 0 && (
        <p className="text-xs text-gray-500 italic">
          No subcategories available for selected category
        </p>
      )}
    </div>
  );
};

export default CategoryFilter;
